import * as Types from '../../../../types';
import { REPORT_SOURCE_TYPES } from './useIssueCategoriesByIntakeMethod';
import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { isRequiredFeatureEnabled } from 'src/components/auth';
import { LocalFilterOptions } from 'src/components/organisms';
import {
  CasesOverTimeDataType,
  DonutAndBarChartDataType,
  HorizontalBarChartDataType,
  useCreateAuditEntryMutation,
} from 'src/graphql';
import { isDownloadType, roundPercentages, saveData } from 'src/utils';
import { CHART_IDS } from 'src/utils/analytics';

export const useOperationalHealthDownloads = (
  caseTTR?: CasesOverTimeDataType[],
  categoriesByIntakeMethod?: DonutAndBarChartDataType[],
  substantiationData?: HorizontalBarChartDataType[],
) => {
  const features = useSelector((state: Types.State) => state.features);
  const organisation = useSelector(
    (state: Types.State) => state.organisation?.organisation,
  );
  const segmentEnabled = isRequiredFeatureEnabled(
    features,
    Types.FeatureFlag.FRONTEND_SEGMENT_TRACKING,
  );
  const createAuditEntryMutation = useCreateAuditEntryMutation();

  const downloadCaseTTR = useCallback(
    (
      label: string | number | boolean,
      selectedLocalFilters?: LocalFilterOptions,
      print?: () => void,
    ) => {
      const remappedData: Record<string, string | number>[] =
        caseTTR?.map((data, idx) => {
          return {
            'Duration (days)':
              idx === caseTTR.length - 1
                ? `${data.bar}+`
                : `${data.bar}-${Number(caseTTR[idx + 1].bar) - 1}`,
            'Cases open': data.open,
            'Cases closed': data.closed,
            'Total cases': data.total,
          };
        }) ?? [];

      if (isDownloadType(label)) {
        saveData(
          label,
          organisation,
          segmentEnabled,
          selectedLocalFilters,
          {
            dashboard: 'operational',
            title: 'Cases time to resolution',
            data: remappedData,
            svgId: CHART_IDS.case_time_to_resolution,
          },
          createAuditEntryMutation,
          print,
        );
      }
    },
    [caseTTR, createAuditEntryMutation, organisation, segmentEnabled],
  );

  const downloadCategoriesByIntakeMethod = useCallback(
    (
      label: string | number | boolean,
      selectedLocalFilters?: LocalFilterOptions,
      print?: () => void,
    ) => {
      const remappedData =
        categoriesByIntakeMethod?.flatMap((department) => {
          const total = department.categories.reduce((a, b) => a + b.total, 0);
          const percentages = roundPercentages(
            department.categories.map((category) =>
              total > 0 ? (category.total / total) * 100 : 0,
            ),
          );
          return department.categories.map((category, index) => ({
            'Intake method':
              Types.REPORT_SOURCE[
                department.donutSegment as REPORT_SOURCE_TYPES
              ],
            'Issue category name': category.category,
            'Issue category count': category.total,
            'Intake Method percentage': `${percentages[index]}%`,
          }));
        }) ?? [];

      if (isDownloadType(label)) {
        saveData(
          label,
          organisation,
          segmentEnabled,
          selectedLocalFilters,
          {
            dashboard: 'operational',
            title: 'Issue categories by intake method',
            data: remappedData,
            svgId: CHART_IDS.intake_methods_per_category,
          },
          createAuditEntryMutation,
          print,
        );
      }
    },
    [
      categoriesByIntakeMethod,
      createAuditEntryMutation,
      organisation,
      segmentEnabled,
    ],
  );

  const downloadSubstantiationData = useCallback(
    (
      label: string | number | boolean,
      selectedLocalFilters?: LocalFilterOptions,
      print?: () => void,
    ) => {
      const total = substantiationData?.reduce(
        (prev, current) => prev + current.value,
        0,
      );
      const percentages = roundPercentages(
        substantiationData?.map((data) =>
          total ? (data.value / total) * 100 : 0,
        ) ?? [],
      );
      const remappedData: Record<string, string | number>[] =
        substantiationData?.map((data, index) => {
          return {
            'Substantiation status': data.label,
            'Total reports': data.value,
            'Overall percentage': `${percentages[index]}%`,
          };
        }) ?? [];

      if (isDownloadType(label)) {
        saveData(
          label,
          organisation,
          segmentEnabled,
          selectedLocalFilters,
          {
            dashboard: 'operational',
            title: `Substantiation cases across ${organisation?.name}`,
            data: remappedData,
            svgId: CHART_IDS.report_substantiation_totals,
          },
          createAuditEntryMutation,
          print,
        );
      }
    },
    [
      substantiationData,
      organisation,
      segmentEnabled,
      createAuditEntryMutation,
    ],
  );

  return {
    downloadCaseTTR,
    downloadCategoriesByIntakeMethod,
    downloadSubstantiationData,
  };
};
